import { useSelector } from 'react-redux'
import { Bar, Line } from 'recharts'
import { CalculateSize } from '../../../../../../../../common/CalculateSize'
import { ChartContainer } from '../../../../../../../../common/chart/chartContainer'
import { Legend } from '../../../../../../../../common/chart/legend'
import { Loading } from '../../../../../../../../common/loading'
import { Notification } from '../../../../../../../../common/notification'
import { SCREEN } from '../../../../../../../../common/selectCurrencyUnit/constant'
import { useFormatCurrency } from '../../../../../../../../common/selectCurrencyUnit/useFormatCurrency'
import { keyBy } from '../../../../../../../../helpers/common'
import { convertData, formatVal } from '../../../../../../../../helpers/value'
import { CURRENCY } from '../../../../../../../../utils/currency'
import {
  selectIsLoading,
  selectIsShowValueData,
  selectRawData,
  selectYears,
} from '../../../store/slice'

export const Content = () => {
  const { getExchangeRate, multiplier, currency, formatValueWithUnit } =
    useFormatCurrency(SCREEN.INDUSTRY_OVERVIEW)

  const isLoading = useSelector(selectIsLoading)
  const isShow = useSelector(selectIsShowValueData)
  const data = useSelector(selectRawData)
  const years = useSelector(selectYears)
  const locale = useSelector((state) => state.i18n.locale)

  const dataLevel0 = keyBy(
    data.filter((item) => item.vsicId === 0),
    'year',
  )
  const dataMain = keyBy(
    data.filter((item) => item.isMain),
    'year',
  )

  const data1 = []
  const data2 = []

  years.forEach((year) => {
    const mainItem = dataMain[year] || {}
    const level0Item = dataLevel0[year] || {}

    data1.push({
      year,
      name: mainItem.vsicName,
      data1: mainItem.import,
      data2: mainItem.export,
      data3: mainItem.balanceOfTrade,
    })
    data2.push({
      year,
      name: level0Item.vsicName,
      data4: level0Item.import,
      data5: level0Item.export,
      data6: level0Item.balanceOfTrade,
    })
  })

  const indicatorById = {}
  Array.from(Array(6).keys()).forEach((_, index) => {
    indicatorById[`data${index + 1}`] = { exchangeRateType: 'averageRate' }
  })

  const data1WithCurrency = convertData(
    data1,
    getExchangeRate,
    indicatorById,
    multiplier,
    ['name', 'year'],
  )
  const data2WithCurrency = convertData(
    data2,
    getExchangeRate,
    indicatorById,
    multiplier,
    ['name', 'year'],
  )

  const getContentTooltip = () => {
    const result = [
      {
        dataKey: 'data1',
        label: 'industryOverview.macroeconomics.IMPORT',
        isI18n: true,
        formatValue: (value) =>
          formatValueWithUnit({ value, isFinancialStatement: false }),
      },
      {
        dataKey: 'data2',
        label: 'industryOverview.macroeconomics.EXPORT',
        isI18n: true,
        formatValue: (value) =>
          formatValueWithUnit({ value, isFinancialStatement: false }),
      },
      {
        dataKey: 'data3',
        label: 'industryOverview.macroeconomics.BALANCE_OF_TRADE',
        isI18n: true,
        formatValue: (value) =>
          formatValueWithUnit({ value, isFinancialStatement: false }),
      },
    ]

    if (isShow) {
      return [
        ...result,
        {
          dataKey: 'data4',
          label: 'industryOverview.macroeconomics.IMPORT_ALL_INDUSTRIES',
          isI18n: true,
          formatValue: (value) =>
            formatValueWithUnit({ value, isFinancialStatement: false }),
        },
        {
          dataKey: 'data5',
          label: 'industryOverview.macroeconomics.EXPORT_ALL_INDUSTRIES',
          isI18n: true,
          formatValue: (value) =>
            formatValueWithUnit({ value, isFinancialStatement: false }),
        },
        {
          dataKey: 'data6',
          label:
            'industryOverview.macroeconomics.BALANCE_OF_TRADE_ALL_INDUSTRIES',
          isI18n: true,
          formatValue: (value) =>
            formatValueWithUnit({ value, isFinancialStatement: false }),
        },
      ]
    }

    return result
  }

  if (isLoading) {
    return (
      <div className="h100 positionRelative">
        <Loading />
      </div>
    )
  }

  if (!data1WithCurrency.length && !data2WithCurrency.length) {
    return <Notification />
  }

  return (
    <div className="flexColumn h100">
      <div className="flex1 positionRelative">
        <CalculateSize height="100%">
          {(size) => {
            return (
              <ChartContainer
                data={data1WithCurrency.map((_, index) => ({
                  ...data1WithCurrency[index],
                  ...data2WithCurrency[index],
                }))}
                width={'100%'}
                height={size.height}
                xAxis={[
                  {
                    dataKey: 'year',
                    height: 30,
                    allowDuplicatedCategory: false,
                  },
                ]}
                yAxis={[
                  {
                    yAxisId: 'left',
                    dataKeys: isShow
                      ? ['data1', 'data2', 'data3', 'data4', 'data5', 'data6']
                      : ['data1', 'data2', 'data3'],
                    isBarChart: true,
                    tickFormatter: (value) =>
                      formatVal(
                        value,
                        locale,
                        undefined,
                        currency === CURRENCY.VND ? 0 : 2,
                      ),
                  },
                ]}
                tooltip={{
                  schema: {
                    content: getContentTooltip(),
                  },
                }}
              >
                <Bar
                  yAxisId={'left'}
                  dataKey={'data1'}
                  barSize={25}
                  fill="#006AA6"
                />
                <Bar
                  yAxisId={'left'}
                  dataKey={'data2'}
                  barSize={25}
                  fill="#FFCD60"
                />

                <Line
                  yAxisId={'left'}
                  dataKey={'data3'}
                  stroke="#00A8B5"
                  strokeWidth={2}
                  dot={false}
                  activeDot={false}
                />
                {isShow && (
                  <>
                    <Bar
                      yAxisId={'left'}
                      dataKey={'data4'}
                      barSize={25}
                      fill="#7F63B9"
                    />
                    <Bar
                      yAxisId={'left'}
                      dataKey={'data5'}
                      barSize={25}
                      fill="#C06C84"
                    />
                    <Line
                      yAxisId={'left'}
                      dataKey={'data6'}
                      stroke="#F49966"
                      strokeWidth={2}
                      dot={false}
                      activeDot={false}
                    />
                  </>
                )}
              </ChartContainer>
            )
          }}
        </CalculateSize>
      </div>
      <div className="flexColumn gap1">
        <Legend
          className="dFlex gap2"
          items={[
            {
              key: 1,
              color: '#006AA6',
              title: 'industryOverview.macroeconomics.IMPORT',
            },
            {
              key: 2,
              color: '#FFCD60',
              title: 'industryOverview.macroeconomics.EXPORT',
            },
            {
              key: 3,
              color: '#00A8B5',
              title: 'industryOverview.macroeconomics.BALANCE_OF_TRADE',
            },
          ]}
        />
        {isShow && (
          <Legend
            className="dFlex gap2"
            items={[
              {
                key: 4,
                color: '#FF596A',
                title: 'industryOverview.macroeconomics.IMPORT_ALL_INDUSTRIES',
              },
              {
                key: 5,
                color: '#7F63B9',
                title: 'industryOverview.macroeconomics.EXPORT_ALL_INDUSTRIES',
              },
              {
                key: 6,
                color: '#B3CC85',
                title:
                  'industryOverview.macroeconomics.BALANCE_OF_TRADE_ALL_INDUSTRIES',
              },
            ]}
          />
        )}
      </div>
    </div>
  )
}
